import { type SxProps, type Theme } from "@mui/material/styles";

import Icons from "assets";
import { Accordion } from "ui/accordion";
import { Box } from "ui/box";
import { Checkbox } from "ui/checkbox";
import { Panel } from "ui/panel";
import { Popover } from "ui/popover";
import { TextButton } from "ui/text-button";

import { useOpen } from "hooks/use-open";

import { FilterPanelProps } from "./filter-panel.types";

const OPEN_TRIGGER_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.primary.dark });

/** Items 12px apart — Figma's `popover/container/body/item/body/spacing`. */
const FILTER_LIST_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

/**
 * Figma's checkbox rows are 32px: the 20px box inside its 6px `container/p`. The theme negates that
 * padding so the halo costs no layout, so it comes back here — as the list's inset and the gap between
 * rows.
 */
const OPTION_LIST_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  p: 0.75,
};

const FilterPanel = ({
  label = "Filters",
  filters,
  currentFilters,
  onFilterChange,
  beforeFilters,
  isFetching = false,
  triggerTestId = "all-filters-button",
}: FilterPanelProps) => {
  const { open, handleClose, handleToggle } = useOpen(false);

  return (
    <Popover
      padding={0}
      placement="bottom-start"
      width={320}
      open={open}
      onClose={() => {
        if (!isFetching) {
          handleClose();
        }
      }}
      triggerElement={
        <TextButton
          startIcon={<Icons.Filter />}
          data-testid={triggerTestId}
          sx={open ? OPEN_TRIGGER_SX : undefined}
          onClick={handleToggle}>
          {label}
        </TextButton>
      }>
      <Panel title={label}>
        <Box sx={FILTER_LIST_SX}>
          {beforeFilters}

          {filters.map((filter) => (
            <Accordion key={filter.filterId} variant="filter" title={filter.title} defaultExpanded>
              <Box sx={OPTION_LIST_SX}>
                {filter.options.map((option) => {
                  const isSelected = (
                    (currentFilters[filter.filterId as keyof typeof currentFilters] as string[]) ?? []
                  ).includes(option.name);

                  return (
                    <Checkbox
                      key={option.name}
                      size="sm"
                      label={option.description}
                      checked={isSelected}
                      onChange={() => onFilterChange(filter, option)}
                    />
                  );
                })}
              </Box>
            </Accordion>
          ))}
        </Box>
      </Panel>
    </Popover>
  );
};

export default FilterPanel;
